import React, { useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { cardSub, titleRow, gridTitleSub, gridHint, colors, won, agGridBox } from '../styles/fdsTheme';

/**
 * 전략별 성과 화면 상단 — 슬롯 설명 (2026-10-23).
 *
 * 슬롯 목록·파라미터·배정자금은 API(/pfstrat/summary)의 값을 그대로 쓰고,
 * 전략 규칙 설명만 여기 코드(code)별로 둔다. 규칙의 정본은 C# 전략 클래스다:
 *   I1 IntradayEmaScalpingStrategy · I2 IntradayVwapStrategy
 *   I3 IntradayRsiStrategy        · I4 IntradayDonchianStrategy
 * 시스템 슬롯(MANUAL·ENS·TA)은 API 가 내려주지 않으므로 여기에도 없다.
 */

export interface GuideSlot {
  id: number;
  name: string;
  code?: string;
  stratName?: string;
  allocAmt: number;
  enabled: boolean;
  startD?: string;
  params?: string | Record<string, unknown> | null;
}

type P = Record<string, number>;

interface StratDoc {
  title: string;
  idea: string;
  buy: (p: P) => string;
  sell: (p: P) => string;
  note?: string;
}

const num = (p: P, k: string, dflt: number) => (typeof p[k] === 'number' ? p[k] : dflt);

const DOCS: Record<string, StratDoc> = {
  I1: {
    title: 'EMA 크로스',
    idea: '짧은 평균이 긴 평균을 뚫는 순간을 추세 전환으로 본다.',
    buy: p => `EMA${num(p, 'FastPeriod', 5)} 가 EMA${num(p, 'SlowPeriod', 20)} 를 위로 교차 (골든크로스)`,
    sell: p => `EMA${num(p, 'FastPeriod', 5)} 가 EMA${num(p, 'SlowPeriod', 20)} 를 아래로 교차 (데드크로스)`,
    note: '평균선이 스치기만 해도 신호가 나 매매가 잦다 — 백테스트(05-13~09-11)에서 2,320회, 수수료·휩소로 −93만원.',
  },
  I2: {
    title: 'VWAP 이탈',
    idea: '당일 거래량가중평균가(VWAP)보다 확실히 위면 매수세, 아래면 매도세로 본다.',
    buy: p => `종가가 VWAP 보다 +${num(p, 'Threshold', 0.3)}% 이상 위 · 최근 5분 거래량이 당일 평균의 1.2배 초과`,
    sell: p => `종가가 VWAP 보다 −${num(p, 'Threshold', 0.3)}% 이상 아래`,
    note: 'VWAP 은 매일 09:00 부터 새로 누적한다.',
  },
  I3: {
    title: 'RSI 역추세',
    idea: '과하게 빠지면 반등, 과하게 오르면 되돌림을 노린다 (추세와 반대로 진입).',
    buy: p => `RSI(${num(p, 'Period', 14)}) ≤ ${num(p, 'Oversold', 30)} (과매도)`,
    sell: p => `RSI(${num(p, 'Period', 14)}) ≥ ${num(p, 'Overbought', 70)} (과매수)`,
    note: "Wilder 스무딩. 처음 N봉은 워밍업이라 신호가 없다.",
  },
  I4: {
    title: 'Donchian 채널 돌파',
    idea: '최근 N분 만의 신고가 = 추세 시작, 신저가 = 추세 꺾임.',
    buy: p => `종가가 최근 ${num(p, 'Period', 90)}분(봉) 최고가를 돌파`,
    sell: p => `종가가 최근 ${num(p, 'Period', 90)}분(봉) 최저가를 이탈`,
    note: '진짜 돌파만 잡아 거래가 적다 — 백테스트 314회 · +61만원(승률 47.8%, PF 1.27). '
      + 'N봉이 쌓이기 전(개장 직후 90분)과 채널 안에서는 신호가 없다.',
  },
};

const parseParams = (raw: GuideSlot['params']): P => {
  if (!raw) return {};
  try {
    const o = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const out: P = {};
    Object.entries(o ?? {}).forEach(([k, v]) => { if (typeof v === 'number') out[k] = v; });
    return out;
  } catch { return {}; }
};

interface GuideRow {
  id: number;
  name: string;
  strat: string;
  idea: string;
  buy: string;
  sell: string;
  allocAmt: number;
  buyAmt: number | null;
  buyWei: number | null;
  maxAmt: number | null;
  maxWei: number | null;
  startD: string;
  note: string;
  enabled: boolean;
}

const wrap: ColDef<GuideRow> = { wrapText: true, autoHeight: true, cellStyle: { lineHeight: '1.5', paddingTop: 4, paddingBottom: 4 } };
const right = { textAlign: 'right' } as const;

export default function StrategySlotGuide({ slots, selectedId, onSelect }: {
  slots: GuideSlot[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
}) {
  const [open, setOpen] = useState(true);

  const rows = useMemo<GuideRow[]>(() => slots.map(s => {
    const p = parseParams(s.params);
    const doc = s.code ? DOCS[s.code] : undefined;
    const buyWei = p.BuyWeiPct ?? null;
    const maxWei = p.MaxPosWeiPct ?? null;
    return {
      id: s.id, name: s.name, enabled: s.enabled,
      strat: doc?.title ?? s.stratName ?? s.code ?? '',
      idea: doc?.idea ?? '',
      buy: doc ? doc.buy(p) : `규칙 설명 없음 (${s.code ?? '코드 미지정'})`,
      sell: doc ? doc.sell(p) : '',
      allocAmt: s.allocAmt,
      buyWei, buyAmt: buyWei != null ? s.allocAmt * buyWei / 100 : null,
      maxWei, maxAmt: maxWei != null ? s.allocAmt * maxWei / 100 : null,
      startD: s.startD ?? '',
      note: doc?.note ?? '',
    };
  }), [slots]);

  const cols = useMemo<ColDef<GuideRow>[]>(() => [
    { field: 'name', headerName: '슬롯', width: 105, pinned: 'left',
      cellStyle: { fontWeight: 600, color: colors.titleSub },
      valueFormatter: p => (p.data?.enabled ? p.value : `${p.value} (중지)`) },
    { field: 'strat', headerName: '전략', width: 115 },
    { field: 'idea', headerName: '개념', flex: 1.4, minWidth: 200, ...wrap },
    { field: 'buy', headerName: '매수 조건', flex: 1.3, minWidth: 190, ...wrap,
      cellStyle: { ...(wrap.cellStyle as object), color: colors.up } },
    { field: 'sell', headerName: '매도 조건', flex: 1.1, minWidth: 170, ...wrap,
      cellStyle: { ...(wrap.cellStyle as object), color: colors.down } },
    { field: 'allocAmt', headerName: '배정자금', width: 95, cellStyle: right, valueFormatter: p => won(p.value) },
    { field: 'buyAmt', headerName: '1회 매수', width: 110, cellStyle: right,
      valueFormatter: p => (p.value == null ? '-' : `${won(p.value)} (${p.data?.buyWei}%)`) },
    { field: 'maxAmt', headerName: '종목당 최대', width: 115, cellStyle: right,
      valueFormatter: p => (p.value == null ? '-' : `${won(p.value)} (${p.data?.maxWei}%)`) },
    { field: 'startD', headerName: '시작', width: 95, cellStyle: { textAlign: 'center' } },
    { field: 'note', headerName: '비고', flex: 1.6, minWidth: 220, ...wrap,
      cellStyle: { ...(wrap.cellStyle as object), color: colors.muted } },
  ], []);

  if (slots.length === 0) return null;

  return (
    <div style={cardSub}>
      <div style={{ ...titleRow, cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <h3 style={gridTitleSub}>
          {open ? '▾' : '▸'} 슬롯 설명
          <span style={gridHint}>(전략 규칙 · 파라미터 · 공통 운용 규칙 — 행 클릭: 그 슬롯만 보기)</span>
        </h3>
      </div>

      {open && (
        <>
          <div className="ag-theme-quartz" style={{ ...agGridBox('auto'), width: '100%' }}>
            <AgGridReact<GuideRow>
              rowData={rows}
              columnDefs={cols}
              defaultColDef={{ sortable: false, resizable: true, filter: false }}
              domLayout="autoHeight"
              animateRows={false}
              enableCellTextSelection={true}
              onRowClicked={e => { if (e.data) onSelect(selectedId === e.data.id ? null : e.data.id); }}
              getRowStyle={p => (p.data?.id === selectedId
                ? { backgroundColor: '#f5f3ff', fontWeight: 600 }
                : p.data && !p.data.enabled ? { opacity: 0.55 } : undefined)}
            />
          </div>

          <div style={{ marginTop: 10, fontSize: '11px', color: colors.muted, lineHeight: 1.7 }}>
            <b style={{ color: colors.titleSub }}>공통 운용 규칙</b>{' '}
            · 대상: 관심종목 ETF 그룹, 1분봉으로 매분 판단
            · 슬롯마다 따로 판단·주문 — 같은 종목을 여러 슬롯이 동시에 보유할 수 있다 (상계 없음)
            · 매도는 그 슬롯 보유분 안에서만 · 같은 종목·같은 방향은 10분 쿨다운
            · 신규 진입은 15:00 까지, 15:05 부터 슬롯별 보유 전량 청산 (당일 오버나잇 없음)
            · 손절·익절은 현재 꺼짐
            · 성과 = 체결 기준 평균단가, 수수료 0.015% 추정 · 계좌 실손익은 증권사 순자산(NAV) 변화
          </div>
        </>
      )}
    </div>
  );
}
